import type { BlockDefinition, BlockPrivateRootContext, BlockSerialized } from '#layers/block-engine/types/block';
import { defineComponent, h, inject, provide, useTemplateRef, watch } from 'vue';
import { isNullishOrEmpty, mapValues } from '#layers/base/public';
import { useBlockResolverStore } from '#layers/block-engine/stores/block-resolver';
import { createBlockRootContextApi } from '#layers/block-engine/utils/block/api/root-context';
import { emptyBlockSlot } from '#layers/block-engine/utils/block/const';
import { isBlockDefinition, isBlockSerialized, isBlockSerializedChild } from '#layers/block-engine/utils/block/editor';
import { createBlockSerializedRenderComponent } from '#layers/block-engine/utils/block/render';
import { createBlockDefinitionRenderComponent } from './BlockDefinitionRender';
import {
	blockPrivateRootContextFallbackKey,
	blockRenderEmits,
	blockRenderProps,
	blockRootContextApiFallbackKey,
	shouldRegisterBlockRootContext,
} from './BlockRenderShared';

export default defineComponent({
	name: 'BlockRenderSerialized',

	props: blockRenderProps,

	emits: blockRenderEmits,

	inheritAttrs: false,

	async setup(props, { attrs, slots, emit }) {
		const genericEmit = emit as ((event: string, payload?: any) => void);
		const blockRef = useTemplateRef('blockRef');
		const inheritedRootContextApi = inject(blockRootContextApiFallbackKey, null);

		let rootBlock = typeof props.block === 'string'
			? await useBlockResolverStore().resolveBlock(props.block)
			: props.block;

		if (!rootBlock) {
			throw new Error(`Block with ID "${props.block}" not found.`);
		}

		if (!isBlockSerialized(rootBlock)) {
			throw new Error(`BlockRenderSerialized only renders serialized root blocks. Received "${rootBlock.id}".`);
		}

		if (props.idOverride != null) {
			rootBlock = {
				...rootBlock,
				id: props.idOverride,
			};
		}

		function getPrivateRootContext(): BlockPrivateRootContext {
			return {
				...props.privateContext,
				// Provide the override in case there are nested `<BlockRender>` in blocks.
				rootBlockOverride: props.privateContext?.rootBlockOverride ?? rootBlock,
			};
		}

		const rootContextApi = createBlockRootContextApi({
			getBaseRootContext: () => props.context,
			getRootBlock: () => getPrivateRootContext().rootBlockOverride ?? rootBlock,
			parentApi: inheritedRootContextApi ?? undefined,
		});

		provide(blockPrivateRootContextFallbackKey, getPrivateRootContext);

		watch(blockRef, (value) => {
			if (value) {
				emit('block:ref', value);
			}
		});

		emit('block:resolve', rootBlock);

		const { resolveBlock } = useBlockResolverStore();
		const childrenBlockPromises: Record<string, ReturnType<typeof resolveBlock>> = Object.create(null);

		/**
		 * Queue a child block resolution once per block ID.
		 */
		function queueChildBlock(blockId: string) {
			const promise = childrenBlockPromises[blockId];

			if (promise) {
				return {
					promise,
					isNew: false,
				};
			}

			const newPromise = resolveBlock(blockId);
			childrenBlockPromises[blockId] = newPromise;

			return {
				promise: newPromise,
				isNew: true,
			};
		}

		/**
		 * Recursively queue serialized child blocks and descend only into newly queued blocks.
		 */
		async function loadBlockChildren(block: BlockSerialized | BlockDefinition) {
			if (!isBlockSerialized(block)) {
				return;
			}

			const childrenBlockIds = [...new Set(
				Object.values(block.children ?? {})
					.filter((b) => isBlockSerializedChild(b))
					.map((c) => c.blockId),
			)];

			await Promise.all(childrenBlockIds.map(async (id) => {
				const { promise, isNew } = queueChildBlock(id);

				if (!isNew) {
					return;
				}

				const childBlock = await promise;
				await loadBlockChildren(childBlock);
			}));
		}

		await loadBlockChildren(rootBlock);

		const result = await Promise.all(Object.values(childrenBlockPromises));
		const childrenBlocks = result.reduce((acc, block) => {
			acc[block.id] = block;
			return acc;
		}, {} as Record<string, BlockSerialized | BlockDefinition>);
		const resolvedBlockComponents: Record<string, any> = {};

		function getBlockRenderComponents() {
			return resolvedBlockComponents;
		}

		const rootBlockComponent = createBlockSerializedRenderComponent({
			block: rootBlock,
			childrenBlocks,
			getBlockRenderComponents,
			// `BlockRender` already owns the root error boundary.
			skipErrorBoundary: true,
			rootContextApi,
			emit: genericEmit,
			registerRootContext: shouldRegisterBlockRootContext({
				registerRootContext: props.registerRootContext,
				inheritedRootContextApi,
			}),
		});

		/**
		 * BlockId => Component
		 */
		Object.assign(resolvedBlockComponents, mapValues(childrenBlocks, (block) => {
			if (isBlockDefinition(block)) {
				return createBlockDefinitionRenderComponent({
					block,
					rootContextApi,
					getRootBlock: () => rootBlock,
					emit: genericEmit,
					skipErrorBoundary: props.skipErrorBoundary,
					unwrapBlockDefinition: true,
				});
			}

			return createBlockSerializedRenderComponent({
				block: block as BlockSerialized,
				childrenBlocks,
				getBlockRenderComponents,
				skipErrorBoundary: props.skipErrorBoundary,
				rootContextApi,
				emit: genericEmit,
			});
		}));

		emit('block:load', rootBlock);

		if (isNullishOrEmpty(rootBlock.rootChildren)) {
			return () => slots[emptyBlockSlot]?.();
		}

		return () => h(rootBlockComponent, {
			...attrs,
			...props.props,
			'data-block-id': typeof props.block === 'string' ? props.block : props.block?.id,
			'style': {
				'--block-spacing': props.spacing,
				...attrs.style as any,
			},
			'ref': 'blockRef',
		}, slots);
	},
});
